import type { ReactNode } from 'react'
import React, { useState } from 'react'

import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'

type Props = {
  items: string[]
  onSort: (oldIndex: number, newIndex: number) => void
  draggedElement: (itemId: string) => ReactNode
  children: ReactNode
}

export const SortableGrid = ({ items, onSort, draggedElement, children }: Props) => {
  const [activeItem, setActiveItem] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  )

  const handleDragStart = ({ active }: DragStartEvent) => {
    const item = items.find((itemId) => itemId == active.id)

    if (item) {
      setActiveItem(item)
    }
  }

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveItem(null)

    const newIndex = items.findIndex((itemId) => itemId === active.id)
    const oldIndex = items.findIndex((itemId) => itemId === over?.id)

    if (newIndex === oldIndex) return

    onSort(newIndex, oldIndex)
  }

  const handleDragCancel = () => {
    setActiveItem(null)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
      {activeItem && <DragOverlay>{draggedElement(activeItem)}</DragOverlay>}
    </DndContext>
  )
}
